import React, { useState, useEffect } from 'react';
import api from '../api/axios';

interface Counterparty {
  id: number;
  name: string;
  phone?: string;
  email?: string;
}

interface Invoice {
  id: number;
  number: string;
  invoice_type: string;
  currency: string;
  amount: number;
  paid_amount: number;
  debt: number;
  status: string;
  due_date?: string;
  created_at: string;
}

interface Transaction {
  id: number;
  transaction_type: string;
  currency: string;
  amount: number;
  description?: string;
  transaction_date: string;
}

interface Contract {
  id: number;
  number: string;
  title: string;
  contract_type: string;
  currency: string;
  total_amount: number;
  status: string;
  created_at: string;
  invoices_total: number;
  invoices_paid: number;
  invoices_debt: number;
}

interface ContractDebt {
  contract_id: number;
  contract_number: string;
  contract_title: string;
  currency: string;
  total_amount: number;
  debt: number;
}

interface Summary {
  total_sales: Record<string, number>;
  total_purchases: Record<string, number>;
  total_paid_by_them: Record<string, number>;
  total_paid_by_us: Record<string, number>;
  debitor_debt: Record<string, number>;
  kreditor_debt: Record<string, number>;
  turnover: Record<string, number>;
  contract_debts: ContractDebt[];
}

interface StatementData {
  counterparty: Counterparty;
  counterparty_type: string;
  contracts: Contract[];
  invoices: Invoice[];
  transactions: Transaction[];
  summary: Summary;
}

const CounterpartyStatement: React.FC = () => {
  const [cpType, setCpType] = useState<'client' | 'supplier'>('client');
  const [clients, setClients] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState<StatementData | null>(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'invoices' | 'transactions' | 'contracts' | 'debts'>('invoices');

  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data));
    api.get('/suppliers').then(r => setSuppliers(r.data));
  }, []);

  const fetchStatement = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const params: any = {};
      if (cpType === 'client') params.client_id = selectedId;
      else params.supplier_id = selectedId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/accounting/counterparty-statement', { params });
      setData(res.data);
    } catch (e) {
      alert('Xato yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    if (!selectedId) return;
    setPdfLoading(true);
    try {
      const params: any = {};
      if (cpType === 'client') params.client_id = selectedId;
      else params.supplier_id = selectedId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await api.get('/accounting/counterparty-statement/pdf', {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const name = data?.counterparty?.name || 'statement';
      link.setAttribute('download', `${name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('PDF yaratishda xato');
    } finally {
      setPdfLoading(false);
    }
  };

  const fmtCurrency = (d: Record<string, number>) => {
    if (!d || Object.keys(d).length === 0) return '0';
    return Object.entries(d).map(([k, v]) => `${v.toLocaleString()} ${k.toUpperCase()}`).join(' | ');
  };

  const fmtNum = (n: number) => (n || 0).toLocaleString('uz-UZ', { minimumFractionDigits: 2 });

  const statusColor = (s: string) => {
    const map: Record<string, string> = {
      paid: 'bg-green-100 text-green-700',
      partial: 'bg-yellow-100 text-yellow-700',
      overdue: 'bg-red-100 text-red-700',
      draft: 'bg-gray-100 text-gray-600',
      sent: 'bg-blue-100 text-blue-700',
      active: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
    };
    return map[s] || 'bg-gray-100 text-gray-600';
  };

  const list = cpType === 'client' ? clients : suppliers;
  const contractDebts = data?.summary?.contract_debts || [];

  return (
    <div className="p-3 md:p-6 max-w-7xl mx-auto">
      <h1 className="text-lg md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">Kontragent hisoboti</h1>

      {/* Filtr panel */}
      <div className="bg-white rounded-xl shadow p-4 md:p-5 mb-4 md:mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Turi</label>
            <div className="flex gap-2">
              <button
                onClick={() => { setCpType('client'); setSelectedId(null); setData(null); }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${cpType === 'client' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Mijoz
              </button>
              <button
                onClick={() => { setCpType('supplier'); setSelectedId(null); setData(null); }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${cpType === 'supplier' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Yetkazib beruvchi
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {cpType === 'client' ? 'Mijoz' : 'Yetkazib beruvchi'}
            </label>
            <select
              value={selectedId || ''}
              onChange={e => setSelectedId(Number(e.target.value) || null)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— Tanlang —</option>
              {list.map((c: any) => (
                <option key={c.id} value={c.id}>{c.company_name || c.contact_person}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Boshlanish sanasi</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tugash sanasi</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-4">
          <button
            onClick={fetchStatement}
            disabled={!selectedId || loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Yuklanmoqda...' : 'Hisobotni korish'}
          </button>
          {data && (
            <button
              onClick={exportPdf}
              disabled={pdfLoading}
              className="bg-red-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {pdfLoading ? 'PDF tayyorlanmoqda...' : 'PDF eksport'}
            </button>
          )}
        </div>
      </div>

      {data && (
        <>
          {/* Kontragent info */}
          <div className="bg-white rounded-xl shadow p-4 mb-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-600">
              {data.counterparty_type === 'client' ? 'M' : 'Y'}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">{data.counterparty.name}</h2>
              <p className="text-sm text-gray-500">
                {data.counterparty_type === 'client' ? 'Mijoz' : 'Yetkazib beruvchi'}
                {data.counterparty.phone && ` | ${data.counterparty.phone}`}
                {data.counterparty.email && ` | ${data.counterparty.email}`}
              </p>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <p className="text-xs text-blue-600 font-medium mb-1">Jami sotuvlar</p>
              <p className="text-base font-bold text-blue-800">{fmtCurrency(data.summary.total_sales)}</p>
              <p className="text-xs text-blue-500">Bizdan olgan</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
              <p className="text-xs text-purple-600 font-medium mb-1">Jami xaridlar</p>
              <p className="text-base font-bold text-purple-800">{fmtCurrency(data.summary.total_purchases)}</p>
              <p className="text-xs text-purple-500">Bizga sotgan</p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <p className="text-xs text-indigo-600 font-medium mb-1">Tovar aylanmasi</p>
              <p className="text-base font-bold text-indigo-800">{fmtCurrency(data.summary.turnover)}</p>
              <p className="text-xs text-indigo-500">Jami operatsiyalar</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <p className="text-xs text-green-600 font-medium mb-1">Ular tolagan</p>
              <p className="text-base font-bold text-green-800">{fmtCurrency(data.summary.total_paid_by_them)}</p>
              <p className="text-xs text-green-500">Bizga kelgan tolovlar</p>
            </div>
            <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
              <p className="text-xs text-teal-600 font-medium mb-1">Biz tolagan</p>
              <p className="text-base font-bold text-teal-800">{fmtCurrency(data.summary.total_paid_by_us)}</p>
              <p className="text-xs text-teal-500">Ulardan ketgan tolovlar</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
              <p className="text-xs text-yellow-600 font-medium mb-1">Debitor qarz</p>
              <p className="text-base font-bold text-yellow-800">{fmtCurrency(data.summary.debitor_debt)}</p>
              <p className="text-xs text-yellow-500">Ular bizga qarzdor</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
              <p className="text-xs text-red-600 font-medium mb-1">Kreditor qarz</p>
              <p className="text-base font-bold text-red-800">{fmtCurrency(data.summary.kreditor_debt)}</p>
              <p className="text-xs text-red-500">Biz ularga qarzdormiz</p>
            </div>
            {contractDebts.length > 0 && (
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                <p className="text-xs text-orange-600 font-medium mb-1">Shartnoma qarzlari</p>
                <p className="text-base font-bold text-orange-800">{contractDebts.length} ta shartnoma</p>
                <p className="text-xs text-orange-500">Tolov kutilmoqda</p>
              </div>
            )}
          </div>

          {/* Tablar */}
          <div className="bg-white rounded-xl shadow">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { key: 'invoices', label: `Invoicelar (${data.invoices.length})` },
                { key: 'transactions', label: `Tranzaksiyalar (${data.transactions.length})` },
                { key: 'contracts', label: `Shartnomalar (${data.contracts.length})` },
                { key: 'debts', label: `Shartnoma qarzlari (${contractDebts.length})` },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key as any)}
                  className={`px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition ${
                    activeTab === t.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-4">
              {/* Invoicelar */}
              {activeTab === 'invoices' && (
                <div>
                  {data.invoices.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">Invoice topilmadi</p>
                  ) : (
                    <>
                      {/* Desktop jadval */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-3 py-2 text-left">Raqam</th>
                              <th className="px-3 py-2 text-left">Turi</th>
                              <th className="px-3 py-2 text-right">Summa</th>
                              <th className="px-3 py-2 text-right">Tolangan</th>
                              <th className="px-3 py-2 text-right">Qarz</th>
                              <th className="px-3 py-2 text-left">Holat</th>
                              <th className="px-3 py-2 text-left">Sana</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.invoices.map(inv => (
                              <tr key={inv.id} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="px-3 py-2 font-medium">{inv.number}</td>
                                <td className="px-3 py-2 text-gray-500">{inv.invoice_type}</td>
                                <td className="px-3 py-2 text-right">{fmtNum(inv.amount)} {inv.currency?.toUpperCase()}</td>
                                <td className="px-3 py-2 text-right text-green-600">{fmtNum(inv.paid_amount)}</td>
                                <td className={`px-3 py-2 text-right font-medium ${inv.debt > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {fmtNum(inv.debt)}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${statusColor(inv.status)}`}>
                                    {inv.status}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-400">{inv.created_at?.slice(0, 10)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-gray-50 font-bold border-t-2 border-gray-200">
                              <td colSpan={2} className="px-3 py-2">Jami</td>
                              <td className="px-3 py-2 text-right">{fmtNum(data.invoices.reduce((s, i) => s + i.amount, 0))}</td>
                              <td className="px-3 py-2 text-right text-green-600">{fmtNum(data.invoices.reduce((s, i) => s + i.paid_amount, 0))}</td>
                              <td className="px-3 py-2 text-right text-red-600">{fmtNum(data.invoices.reduce((s, i) => s + i.debt, 0))}</td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Mobil kartalar */}
                      <div className="md:hidden space-y-2">
                        {data.invoices.map(inv => (
                          <div key={inv.id} className="border border-gray-100 rounded-lg p-3">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-medium text-sm">{inv.number}</p>
                                <p className="text-xs text-gray-500">{inv.invoice_type}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${statusColor(inv.status)}`}>
                                {inv.status}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div>
                                <p className="text-gray-400">Summa</p>
                                <p className="font-medium">{fmtNum(inv.amount)} {inv.currency?.toUpperCase()}</p>
                              </div>
                              <div>
                                <p className="text-gray-400">Tolangan</p>
                                <p className="text-green-600 font-medium">{fmtNum(inv.paid_amount)}</p>
                              </div>
                              <div>
                                <p className="text-gray-400">Qarz</p>
                                <p className={`font-medium ${inv.debt > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtNum(inv.debt)}</p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 mt-2">{inv.created_at?.slice(0, 10)}</p>
                          </div>
                        ))}
                        <div className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50 font-bold">
                          <p className="text-xs mb-2">Jami</p>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <p className="text-gray-500 font-normal">Summa</p>
                              <p>{fmtNum(data.invoices.reduce((s, i) => s + i.amount, 0))}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 font-normal">Tolangan</p>
                              <p className="text-green-600">{fmtNum(data.invoices.reduce((s, i) => s + i.paid_amount, 0))}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 font-normal">Qarz</p>
                              <p className="text-red-600">{fmtNum(data.invoices.reduce((s, i) => s + i.debt, 0))}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Tranzaksiyalar */}
              {activeTab === 'transactions' && (
                <div>
                  {data.transactions.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">Tranzaksiya topilmadi</p>
                  ) : (
                    <>
                      {/* Desktop jadval */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-3 py-2 text-left">Turi</th>
                              <th className="px-3 py-2 text-right">Summa</th>
                              <th className="px-3 py-2 text-left">Izoh</th>
                              <th className="px-3 py-2 text-left">Sana</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.transactions.map(tx => (
                              <tr key={tx.id} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${tx.transaction_type === 'incoming' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {tx.transaction_type === 'incoming' ? 'Kirim' : 'Chiqim'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right font-medium">
                                  {fmtNum(tx.amount)} {tx.currency?.toUpperCase()}
                                </td>
                                <td className="px-3 py-2 text-gray-500">{tx.description}</td>
                                <td className="px-3 py-2 text-gray-400">{tx.transaction_date?.slice(0, 10)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobil kartalar */}
                      <div className="md:hidden space-y-2">
                        {data.transactions.map(tx => (
                          <div key={tx.id} className="border border-gray-100 rounded-lg p-3 flex justify-between items-start gap-3">
                            <div className="min-w-0">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${tx.transaction_type === 'incoming' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {tx.transaction_type === 'incoming' ? 'Kirim' : 'Chiqim'}
                              </span>
                              {tx.description && <p className="text-xs text-gray-500 mt-1">{tx.description}</p>}
                              <p className="text-xs text-gray-400 mt-1">{tx.transaction_date?.slice(0, 10)}</p>
                            </div>
                            <p className="font-medium text-sm text-right whitespace-nowrap">{fmtNum(tx.amount)} {tx.currency?.toUpperCase()}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Shartnomalar */}
              {activeTab === 'contracts' && (
                <div>
                  {data.contracts.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">Shartnoma topilmadi</p>
                  ) : (
                    <>
                      {/* Desktop jadval */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-3 py-2 text-left">Raqam</th>
                              <th className="px-3 py-2 text-left">Sarlavha</th>
                              <th className="px-3 py-2 text-left">Turi</th>
                              <th className="px-3 py-2 text-right">Shartnoma summasi</th>
                              <th className="px-3 py-2 text-right">Invoice jami</th>
                              <th className="px-3 py-2 text-right">Tolangan</th>
                              <th className="px-3 py-2 text-right">Qarz</th>
                              <th className="px-3 py-2 text-left">Holat</th>
                              <th className="px-3 py-2 text-left">Sana</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.contracts.map(c => (
                              <tr key={c.id} className={`border-t border-gray-100 hover:bg-gray-50 ${c.invoices_debt > 0 ? 'bg-red-50' : ''}`}>
                                <td className="px-3 py-2 font-medium">{c.number}</td>
                                <td className="px-3 py-2">{c.title}</td>
                                <td className="px-3 py-2 text-gray-500">{c.contract_type}</td>
                                <td className="px-3 py-2 text-right">{fmtNum(c.total_amount)} {c.currency?.toUpperCase()}</td>
                                <td className="px-3 py-2 text-right">{fmtNum(c.invoices_total)}</td>
                                <td className="px-3 py-2 text-right text-green-600">{fmtNum(c.invoices_paid)}</td>
                                <td className={`px-3 py-2 text-right font-medium ${c.invoices_debt > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                  {c.invoices_debt > 0 ? fmtNum(c.invoices_debt) : '—'}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${statusColor(c.status)}`}>
                                    {c.status}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-400">{c.created_at?.slice(0, 10)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobil kartalar */}
                      <div className="md:hidden space-y-2">
                        {data.contracts.map(c => (
                          <div key={c.id} className={`border rounded-lg p-3 ${c.invoices_debt > 0 ? 'border-red-200 bg-red-50' : 'border-gray-100'}`}>
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-medium text-sm">{c.number}</p>
                                <p className="text-xs text-gray-600">{c.title}</p>
                                <p className="text-xs text-gray-400">{c.contract_type}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${statusColor(c.status)}`}>
                                {c.status}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <p className="text-gray-400">Shartnoma summasi</p>
                                <p className="font-medium">{fmtNum(c.total_amount)} {c.currency?.toUpperCase()}</p>
                              </div>
                              <div>
                                <p className="text-gray-400">Invoice jami</p>
                                <p className="font-medium">{fmtNum(c.invoices_total)}</p>
                              </div>
                              <div>
                                <p className="text-gray-400">Tolangan</p>
                                <p className="text-green-600 font-medium">{fmtNum(c.invoices_paid)}</p>
                              </div>
                              <div>
                                <p className="text-gray-400">Qarz</p>
                                <p className={`font-medium ${c.invoices_debt > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                  {c.invoices_debt > 0 ? fmtNum(c.invoices_debt) : '—'}
                                </p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 mt-2">{c.created_at?.slice(0, 10)}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Shartnoma qarzlari */}
              {activeTab === 'debts' && (
                <div>
                  {contractDebts.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-green-600 font-medium text-lg">Barcha shartnomalar tozalangan!</p>
                      <p className="text-gray-400 text-sm mt-1">Qarzdorlik mavjud emas</p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700 font-medium">
                          Jami {contractDebts.length} ta shartnoma bo'yicha tolov kutilmoqda
                        </p>
                      </div>

                      {/* Desktop jadval */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-3 py-2 text-left">Shartnoma raqami</th>
                              <th className="px-3 py-2 text-left">Sarlavha</th>
                              <th className="px-3 py-2 text-right">Shartnoma summasi</th>
                              <th className="px-3 py-2 text-right">Qarz</th>
                            </tr>
                          </thead>
                          <tbody>
                            {contractDebts.map(cd => (
                              <tr key={cd.contract_id} className="border-t border-gray-100 hover:bg-red-50">
                                <td className="px-3 py-2 font-medium text-blue-600">{cd.contract_number}</td>
                                <td className="px-3 py-2">{cd.contract_title}</td>
                                <td className="px-3 py-2 text-right">{fmtNum(cd.total_amount)} {cd.currency?.toUpperCase()}</td>
                                <td className="px-3 py-2 text-right font-bold text-red-600">
                                  {fmtNum(cd.debt)} {cd.currency?.toUpperCase()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-red-50 font-bold border-t-2 border-red-200">
                              <td colSpan={3} className="px-3 py-2 text-red-700">Jami qarz</td>
                              <td className="px-3 py-2 text-right text-red-700">
                                {fmtNum(contractDebts.reduce((s, d) => s + d.debt, 0))} {contractDebts[0]?.currency?.toUpperCase()}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Mobil kartalar */}
                      <div className="md:hidden space-y-2">
                        {contractDebts.map(cd => (
                          <div key={cd.contract_id} className="border border-red-100 rounded-lg p-3">
                            <p className="font-medium text-sm text-blue-600">{cd.contract_number}</p>
                            <p className="text-xs text-gray-600 mb-2">{cd.contract_title}</p>
                            <div className="flex justify-between items-end text-xs">
                              <div>
                                <p className="text-gray-400">Shartnoma summasi</p>
                                <p className="font-medium">{fmtNum(cd.total_amount)} {cd.currency?.toUpperCase()}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-gray-400">Qarz</p>
                                <p className="font-bold text-red-600">{fmtNum(cd.debt)} {cd.currency?.toUpperCase()}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="border-2 border-red-200 rounded-lg p-3 bg-red-50 flex justify-between items-center">
                          <p className="text-sm font-bold text-red-700">Jami qarz</p>
                          <p className="text-sm font-bold text-red-700">
                            {fmtNum(contractDebts.reduce((s, d) => s + d.debt, 0))} {contractDebts[0]?.currency?.toUpperCase()}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CounterpartyStatement;
